/**
 * Conteúdo de um passo — o MESMO componente no modo execução, na
 * pré-visualização do editor e no manual, para a prévia ser fiel ao que a
 * equipe vai ver (spec 4.4).
 *
 * Aviso logo abaixo do texto, com rótulo ATENÇÃO, borda e `role="alert"`;
 * trecho copiável com botão Copiar que vira "Copiado" por 2 s.
 */
import React, { useEffect, useRef, useState } from "react";
import { FiAlertTriangle, FiCheck, FiCopy } from "react-icons/fi";
import styled from "styled-components";

import { markdownCurtoParaHtml } from "../../../utils/markdownCurto";

const COPIADO_POR_MS = 2000;

export function Markdown({ texto, as = "div", className }) {
  const Tag = as;
  // markdownCurtoParaHtml escapa o texto e passa pelo DOMPurify com lista
  // fechada de tags: o HTML daqui é seguro para injetar.
  // eslint-disable-next-line react/no-danger
  return <Tag className={className} dangerouslySetInnerHTML={{ __html: markdownCurtoParaHtml(texto) }} />;
}

const Copiar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  padding: 8px 8px 8px 12px;
  border: 1px dashed var(--border-strong);
  border-radius: 10px;
  background: var(--surface-2);

  code {
    flex: 1 1 auto;
    min-width: 0;
    font-family: var(--font-mono);
    font-size: ${({ $grande }) => ($grande ? "15px" : "13px")};
    color: var(--ink);
    overflow-wrap: anywhere;
  }

  button {
    flex: 0 0 auto;
    min-height: 32px;
    padding: 0 10px;
    border-radius: 7px;
    border: 1px solid var(--border-strong);
    background: var(--surface);
    color: var(--ink);
    font: inherit;
    font-size: 13px;
    font-weight: 600;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    cursor: pointer;
  }

  button[data-copiado="true"] {
    border-color: var(--success-border);
    color: var(--success-text);
  }
`;

export function BotaoCopiar({ texto, grande = false }) {
  const [copiado, setCopiado] = useState(false);
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);

  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(texto);
    } catch {
      // Sem permissão de área de transferência: o texto está visível para copiar à mão.
      return;
    }
    setCopiado(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setCopiado(false), COPIADO_POR_MS);
  };

  return (
    <Copiar $grande={grande}>
      <code>{texto}</code>
      <button type="button" onClick={copiar} data-copiado={copiado} aria-live="polite">
        {copiado ? <FiCheck size={15} aria-hidden="true" /> : <FiCopy size={15} aria-hidden="true" />}
        {copiado ? "Copiado" : "Copiar"}
      </button>
    </Copiar>
  );
}

const BlocoAviso = styled.div`
  display: flex;
  gap: 10px;
  align-items: flex-start;
  padding: 12px;
  border: 2px solid var(--warning-border);
  border-radius: 12px;
  background: var(--warning-bg);

  .icone {
    flex: 0 0 auto;
    width: 28px;
    height: 28px;
    border-radius: 8px;
    display: grid;
    place-items: center;
    background: var(--warning-solid);
    color: var(--on-warning, #1a1300);
  }

  .rotulo {
    display: block;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.08em;
    color: var(--warning-text);
  }

  .texto {
    font-size: ${({ $grande }) => ($grande ? "17px" : "13.5px")};
    font-weight: 600;
    line-height: 1.45;
    color: var(--ink);

    p {
      margin: 0;
    }
  }
`;

export function AvisoDoPasso({ texto, grande = false }) {
  if (!texto) return null;
  return (
    <BlocoAviso role="alert" $grande={grande}>
      <span className="icone" aria-hidden="true">
        <FiAlertTriangle size={16} />
      </span>
      <div>
        <span className="rotulo">ATENÇÃO</span>
        <Markdown texto={texto} className="texto" />
      </div>
    </BlocoAviso>
  );
}

const Caixa = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ $grande }) => ($grande ? "16px" : "10px")};
  min-width: 0;

  .cabeca {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--ink-3);
  }

  h2 {
    margin: 0;
    font-size: ${({ $grande }) => ($grande ? "clamp(22px, 5.5vw, 30px)" : "16px")};
    line-height: 1.2;
    font-weight: 700;
    color: var(--ink);
  }

  .texto {
    font-size: ${({ $grande }) => ($grande ? "clamp(17px, 4.4vw, 19px)" : "14px")};
    line-height: 1.55;
    color: var(--ink);

    p {
      margin: 0 0 6px;
    }
    code {
      font-family: var(--font-mono);
      font-size: 0.92em;
      padding: 1px 5px;
      border-radius: 5px;
      background: var(--surface-2);
      border: 1px solid var(--border);
    }
    ul {
      margin: 0;
      padding-left: 20px;
    }
  }
`;

/**
 * `passo`: `{numero, titulo_curto, sistema, texto, aviso, trecho_copiavel}`.
 * `grande`: tipografia do modo execução (celular em pé, braço esticado).
 */
export default function PassoConteudo({ passo, grande = false, semTitulo = false, children }) {
  if (!passo) return null;
  const cabeca = [passo.numero ? `Passo ${passo.numero}` : null, passo.sistema].filter(Boolean).join(" · ");
  return (
    <Caixa $grande={grande}>
      {cabeca ? <span className="cabeca">{cabeca}</span> : null}
      {!semTitulo && passo.titulo_curto ? <h2>{passo.titulo_curto}</h2> : null}
      {passo.texto ? <Markdown texto={passo.texto} className="texto" /> : null}
      {passo.trecho_copiavel ? <BotaoCopiar texto={passo.trecho_copiavel} grande={grande} /> : null}
      <AvisoDoPasso texto={passo.aviso} grande={grande} />
      {children}
    </Caixa>
  );
}
